import { useState, useEffect } from 'react';
import { DataStore } from 'aws-amplify';
import { SSCC } from '../models';
import { SSCCProps, convertSsccToProps } from '../datastore/ssccs';
import { SsccStatus } from '../common';
import dayjs from 'dayjs';
import { cleanupDuplicateSSCCs, checkForDuplicateSSCCs } from '../common/helperFunctions/datastoreCleanup';

// Deduplication function to handle DataStore duplicates
function deduplicateSSCCs(ssccs: SSCCProps[]): SSCCProps[] {
  const seen = new Map<string, SSCCProps>();

  ssccs.forEach((sscc) => {
    const existing = seen.get(sscc.id);
    if (!existing) {
      // First occurrence, add it
      seen.set(sscc.id, sscc);
    } else {
      // Duplicate found - keep the one with the latest updatedAt or _lastChangedAt
      // This ensures we keep the most recent version
      const existingTime = existing.staged || existing.open || dayjs(0);
      const currentTime = sscc.staged || sscc.open || dayjs(0);

      if (currentTime.isAfter(existingTime)) {
        console.warn(`Duplicate SSCC detected and replaced: ${sscc.id}`);
        seen.set(sscc.id, sscc);
      } else {
        console.warn(`Duplicate SSCC detected and ignored: ${sscc.id}`);
      }
    }
  });

  return Array.from(seen.values());
}

export default function useObserveSsccs(dateLimit: number | null) {
  const [ssccs, setSsccs] = useState<SSCCProps[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const retrieveData = async () => {
      try {
        const date = dateLimit && dateLimit !== 0 ? dayjs().subtract(dateLimit, 'days') : null;
        const today = dayjs().add(1, 'day').toISOString();
        const endDateString = date?.toISOString() ?? '';

        const entities: SSCC[] = await DataStore.query(SSCC, (c) =>
          c.and((c) => [c.open.between(endDateString, today)])
        );
        console.debug('useObserveSsccs:', entities.length, 'ssccs fetched from DataStore');

        const validSsccs = entities
          .map((sscc) => convertSsccToProps(sscc))
          .filter((sscc): sscc is SSCCProps => sscc !== undefined)
          .filter((sscc) => sscc.status !== SsccStatus.cancelled);

        // Apply deduplication to handle DataStore duplicates
        const deduplicatedSsccs = deduplicateSSCCs(validSsccs);

        if (deduplicatedSsccs.length !== validSsccs.length) {
          console.warn(`Removed ${validSsccs.length - deduplicatedSsccs.length} duplicate SSCCs`);
        }

        setSsccs(deduplicatedSsccs);
      } catch (error) {
        console.error('Error retrieving SSCCs:', error);
      }
    };

    const subscription = DataStore.observe(SSCC).subscribe({
      next: () => {
        console.debug('SSCC DataStore change detected, refreshing data');
        retrieveData();
      },
      error: (error) => {
        console.error('Error observing SSCC changes:', error);
      }
    });

    retrieveData();

    return () => {
      subscription.unsubscribe();
    };
  }, [dateLimit]);

  // Handle network status changes
  useEffect(() => {
    const handleOnline = async () => {
      console.log('Network reconnected - forcing SSCC data refresh');
      setIsOnline(true);

      // Add a small delay to allow DataStore to sync
      setTimeout(async () => {
        try {
          // Check for duplicates and clean them up if found
          const hasDuplicates = await checkForDuplicateSSCCs();
          if (hasDuplicates) {
            console.log('Duplicates detected after reconnection, running cleanup...');
            await cleanupDuplicateSSCCs();
          }

          const date = dateLimit && dateLimit !== 0 ? dayjs().subtract(dateLimit, 'days') : null;
          const today = dayjs().add(1, 'day').toISOString();
          const endDateString = date?.toISOString() ?? '';

          const entities: SSCC[] = await DataStore.query(SSCC, (c) =>
            c.and((c) => [c.open.between(endDateString, today)])
          );
          console.debug('Post-reconnection refresh:', entities.length, 'ssccs fetched');

          const validSsccs = entities
            .map((sscc) => convertSsccToProps(sscc))
            .filter((sscc): sscc is SSCCProps => sscc !== undefined)
            .filter((sscc) => sscc.status !== SsccStatus.cancelled);

          const deduplicatedSsccs = deduplicateSSCCs(validSsccs);
          setSsccs(deduplicatedSsccs);

          console.log('SSCC data refreshed after network reconnection');
        } catch (error) {
          console.error('Error refreshing SSCCs after reconnection:', error);
        }
      }, 3000); // 3 second delay to allow sync and cleanup
    };

    const handleOffline = () => {
      console.log('Network disconnected');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dateLimit]);

  return ssccs;
}
